import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, Eye, EyeOff } from "lucide-react"
import { supabase } from "@/lib/agents/db"
import { MFAChallenge } from "@/components/mfa-challenge"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

const passwordSchema = z.object({
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

type PasswordFormData = z.infer<typeof passwordSchema>

export function ResetPassword() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [passwordVisibility, setPasswordVisibility] = useState({ new: false, confirm: false })
  const [isProcessingHash, setIsProcessingHash] = useState(true)
  const [mfaChallenge, setMfaChallenge] = useState<{ challengeId: string; factors: any[] } | null>(null)

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  })

  useEffect(() => {
    const handleHash = async () => {
      const hash = window.location.hash
      if (hash) {
        try {
          const hashParams = new URLSearchParams(hash.substring(1))
          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          const type = hashParams.get('type')

          if (type === 'recovery' && accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })

            if (sessionError) {
              toast({
                title: "Error",
                description: "Invalid or expired reset link. Please request a new one.",
                variant: "destructive"
              })
              navigate('/login')
              setIsProcessingHash(false)
              return
            }

            const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

            if (aalData?.currentLevel === "aal1" && aalData?.nextLevel === "aal2") {
              const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors()

              if (factorsError) {
                toast({
                  title: "Error",
                  description: "Failed to check MFA status",
                  variant: "destructive"
                })
                setIsProcessingHash(false)
                return
              }

              const verifiedFactors = [...(factorsData.totp || []), ...(factorsData.phone || [])].filter(
                (factor) => factor.status === "verified"
              )

              if (verifiedFactors.length > 0) {
                const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
                  factorId: verifiedFactors[0].id,
                })

                if (challengeError) {
                  toast({
                    title: "Error",
                    description: "Failed to initiate MFA challenge",
                    variant: "destructive"
                  })
                  setIsProcessingHash(false)
                  return
                }

                setMfaChallenge({
                  challengeId: challengeData.id,
                  factors: verifiedFactors,
                })
                setIsProcessingHash(false)
                return
              }
            }

            window.history.replaceState(null, '', window.location.pathname)
          } else if (!type || type !== 'recovery') {
            window.history.replaceState(null, '', window.location.pathname)
          }
        } catch (error) {
          console.error("Error processing hash:", error)
          toast({
            title: "Error",
            description: "Failed to process reset link. Please try again.",
            variant: "destructive"
          })
          navigate('/login')
        }
      }
      setIsProcessingHash(false)
    }

    handleHash()
  }, [navigate, toast])

  const handlePasswordUpdate = async (data: PasswordFormData) => {
    setLoading(true)

    try {
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

      if (aalData?.currentLevel === "aal1" && aalData?.nextLevel === "aal2") {
        toast({
          title: "MFA Required",
          description: "Please complete MFA verification before updating your password.",
          variant: "destructive"
        })
        setLoading(false)
        return
      }

      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      })

      if (error) {
        if (error.message.includes("insufficient_aal")) {
          const { data: factorsData } = await supabase.auth.mfa.listFactors()
          const verifiedFactors = [...(factorsData?.totp || []), ...(factorsData?.phone || [])].filter(
            (factor) => factor.status === "verified"
          )

          if (verifiedFactors.length > 0) {
            const { data: challengeData } = await supabase.auth.mfa.challenge({
              factorId: verifiedFactors[0].id,
            })

            if (challengeData) {
              setMfaChallenge({
                challengeId: challengeData.id,
                factors: verifiedFactors,
              })
              setLoading(false)
              return
            }
          }
        }
        throw error
      }

      toast({
        title: "Success",
        description: "Password updated successfully. You can now log in with your new password."
      })

      form.reset()
      navigate('/login')
    } catch (error) {
      console.error("Error updating password:", error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update password",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  if (isProcessingHash) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Card className="w-[400px]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (mfaChallenge) {
    return (
      <div className="flex h-screen items-center justify-center">
        <MFAChallenge
          challengeId={mfaChallenge.challengeId}
          factors={mfaChallenge.factors}
          onSuccess={async () => {
            setMfaChallenge(null)
            await supabase.auth.refreshSession()
          }}
          onCancel={() => {
            setMfaChallenge(null)
            supabase.auth.signOut()
            navigate('/login')
          }}
        />
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <Card className="w-[400px]">
        <CardHeader>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <form onSubmit={form.handleSubmit(handlePasswordUpdate)}>
          <CardContent>
            <div className="grid w-full items-center gap-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={passwordVisibility.new ? "text" : "password"}
                    placeholder="Enter new password"
                    {...form.register("newPassword")}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setPasswordVisibility(prev => ({ ...prev, new: !prev.new }))}
                  >
                    {passwordVisibility.new ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {form.formState.errors.newPassword && (
                  <p className="text-sm text-destructive">{form.formState.errors.newPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={passwordVisibility.confirm ? "text" : "password"}
                    placeholder="Confirm new password"
                    {...form.register("confirmPassword")}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setPasswordVisibility(prev => ({ ...prev, confirm: !prev.confirm }))}
                  >
                    {passwordVisibility.confirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {form.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive">{form.formState.errors.confirmPassword.message}</p>
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating Password...
                </>
              ) : (
                "Update Password"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
} 